import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FilterToolbarState } from '../filter-toolbar-state';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MtnMap } from '../../mtn-map';
import { BaseComponent } from '../../../core/base-component';
import { FilterGroup } from '../../../core/federation/filter/filter-group';
import { FeatureType } from '../../../core/federation/feature/feature-type.enum';
import { pairwise, startWith } from 'rxjs/operators';
import { MtnMapOptions } from '../../mtn-map-options';
import { MapStyleService } from '../../style/map-style.service';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';

@Component({
  standalone: false,
  selector: 'mtn-map-layers-assembly',
  templateUrl: './map-layers-assembly.component.html',
  styleUrls: ['./map-layers-assembly.component.scss']
})
export class MapLayersAssemblyComponent extends BaseComponent implements OnChanges, OnInit {

  @Input()
  map: MtnMap;
  @Input()
  toolbarState: FilterToolbarState;

  form = new UntypedFormGroup({
    isStoreLayerEnabled: new UntypedFormControl(false),
    isPointsOfInterestLayerEnabled: new UntypedFormControl(false)
  });

  constructor(private mapStyleService: MapStyleService, private hitTrackingService: HitTrackingService) {
    super();
  }

  ngOnInit(): void {
    this.updateForm();
    this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.toolbarState) {
      this.updateForm();
    }
  }

  clearAll(): void {
    this.form.setValue({
      isStoreLayerEnabled: false,
      isPointsOfInterestLayerEnabled: false
    });
  }

  private subscribeToFormChanges(): void {
    this.addSubscription(
      this.form.valueChanges
        .pipe(startWith(this.form.getRawValue()), pairwise())
        .subscribe(([prev, next]: [any, any]) => {
          // Detect store layer toggle
          if (prev.isStoreLayerEnabled !== next.isStoreLayerEnabled) {
            this.trackLayerToggle('Store Layer', next.isStoreLayerEnabled);
          }

          // Detect POI layer toggle
          if (prev.isPointsOfInterestLayerEnabled !== next.isPointsOfInterestLayerEnabled) {
            this.trackLayerToggle('Points of Interest Layer', next.isPointsOfInterestLayerEnabled);
            this.mapStyleService.updatePOI(!this.mapStyleService.getPOIStyle());
          }

          // Existing map options update logic
          const isFeatureTypeChanged = prev.isStoreLayerEnabled !== next.isStoreLayerEnabled;
          const featureType = next.isStoreLayerEnabled ? FeatureType.STORE : null;

          const newOptions: MtnMapOptions = {};
          if (isFeatureTypeChanged) {
            newOptions.featureType = featureType;
            newOptions.filterGroup = new FilterGroup();
          }
          if (prev.isPointsOfInterestLayerEnabled !== next.isPointsOfInterestLayerEnabled) {
            newOptions.isPointsOfInterestEnabled = next.isPointsOfInterestLayerEnabled;
          }

          this.map.setOptions(newOptions);
        })
    );
  }


  private trackLayerToggle(layerName: string, enabled: boolean): void {
    const action = enabled
      ? USER_TRACKING_ACTIONS.LAYER_ENABLED
      : USER_TRACKING_ACTIONS.LAYER_DISABLED;

    const description = USER_TRACKING_ACTION_DESCRIPTIONS[action];

    const actionDescription =
      typeof description === 'function'
        ? description(layerName)
        : typeof description === 'string'
          ? description.replace('${layerName}', layerName)
          : 'Layer toggle action';

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.MAP_LAYERS_ASSEMBLY,
      { layerName, enabled },
      action,
      actionDescription
    ).subscribe({
      next: (hit) => console.log('Layer toggle tracked:', hit),
      error: (err) => console.error('Error tracking layer toggle', err)
    });
  }



  private updateForm(): void {
    this.form.get('isStoreLayerEnabled').setValue(this.toolbarState.isStoreLayerEnabled, { emitEvent: false });
    this.form.get('isPointsOfInterestLayerEnabled').setValue(this.toolbarState.isPointOfInterestLayerEnabled, { emitEvent: false });
  }

}
